import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'

/**
 * CaseReschedule — Scale-parity "Reschedule Case" modal: a two-column flow with
 * an availability booking calendar (left) and a slot picker (right). Pick a
 * date → see the mediator's open Morning / Afternoon / Full-Day slots → select
 * one → Reschedule. Reads availability from CM-be (shared `availability`
 * table); the submit proxies to scale-be so emails / calendar sync / invoice
 * updates fire exactly like Scale.
 */

const GREEN = '#DEFDCC'
const RED = '#FFC0BE'
const TEAL = '#115E59'
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const TZ_SHORT = { 'US/Eastern': 'EST', 'US/Pacific': 'PST', 'US/Central': 'CT', 'US/Mountain': 'MST', HST: 'HST' }

const pad2 = (n) => String(n).padStart(2, '0')
const ymd = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`

function fmtTime(t) {
  if (!t) return ''
  const [hStr, mStr] = String(t).split(':')
  const h = Number(hStr)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const hr = h % 12 === 0 ? 12 : h % 12
  return `${hr}:${pad2(Number(mStr) || 0)} ${ampm}`
}
const timeLabel = (t, tz) => `${fmtTime(t)} (${TZ_SHORT[tz] || tz || ''})`

// Mon–Fri weeks for a month, weekday-aligned (leading nulls so the first day
// sits under the right column), weekends skipped — matching Scale's calendar.
function buildWeeks(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells = []
  for (let d = 1; d <= daysInMonth; d++) {
    const wd = new Date(year, month, d).getDay() // 0 Sun .. 6 Sat
    if (wd === 0 || wd === 6) continue
    cells.push({ day: d, col: wd - 1 })
  }
  const weeks = []
  let week = []
  if (cells.length) for (let i = 0; i < cells[0].col; i++) week.push(null)
  for (const c of cells) {
    week.push(c)
    if (week.length === 5) { weeks.push(week); week = [] }
  }
  if (week.length) { while (week.length < 5) week.push(null); weeks.push(week) }
  return weeks
}

function CalendarCell({ day, booking, disabled, onClick }) {
  const base = {
    position: 'relative', height: 44, width: 44, margin: '4px auto',
    borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer', padding: 0,
  }
  let bg = '#fff'
  if (disabled) bg = RED
  else if (booking === 'fullday') bg = GREEN
  else if (booking === 'booked') bg = RED
  const showTriangle = !disabled && (booking === 'morning' || booking === 'afternoon')
  const tri = booking === 'morning'
    ? { position: 'absolute', top: 0, left: 0, height: 0, width: 0, borderTop: `44px solid ${GREEN}`, borderRight: `44px solid ${RED}` }
    : { position: 'absolute', bottom: 0, right: 0, height: 0, width: 0, borderBottom: `44px solid ${GREEN}`, borderLeft: `44px solid ${RED}` }
  return (
    <button type="button" disabled={disabled} onClick={disabled ? undefined : onClick} style={{ ...base, background: bg }}>
      {showTriangle && <span style={tri} />}
      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#111827', zIndex: 10 }}>{day}</span>
    </button>
  )
}

function SlotButton({ title, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full p-2 text-xs rounded-md shadow border text-black border-gray-300 hover:bg-gray-50 text-left"
    >
      {title}
    </button>
  )
}

// "MM/DD/YY @ h:mmA" — matches Scale's report header timestamp.
function reportTimestamp() {
  const d = new Date()
  let h = d.getHours()
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 === 0 ? 12 : h % 12
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${String(d.getFullYear()).slice(2)} @ ${h}:${pad2(d.getMinutes())}${ampm}`
}

// Derive per-date morning/afternoon availability, drop weekends + past days,
// then expand to rows per the selected slot checkboxes. Port of Scale's
// buildAppointmentReportRows.
function buildAppointmentReportRows(data, selectedSlots) {
  const now = new Date()
  const todayStr = ymd(now.getFullYear(), now.getMonth(), now.getDate())
  const byDate = {}
  for (const item of data || []) {
    if (item.status !== 'available') continue
    const d = item.date
    const [yy, mm, dd] = d.split('-').map(Number)
    const dow = new Date(yy, mm - 1, dd).getDay()
    if (dow === 0 || dow === 6) continue
    if (d < todayStr) continue
    if (!byDate[d]) byDate[d] = { morning: false, afternoon: false }
    if (item.slot === 'morning') byDate[d].morning = true
    else if (item.slot === 'afternoon') byDate[d].afternoon = true
    else if (item.slot === 'fullday') { byDate[d].morning = true; byDate[d].afternoon = true }
  }
  const out = []
  for (const [dateStr, { morning: m, afternoon: a }] of Object.entries(byDate)) {
    const both = m && a
    if (selectedSlots.morning && m) out.push({ date: dateStr, slot: 'morning' })
    if (selectedSlots.afternoon && a) out.push({ date: dateStr, slot: 'afternoon' })
    if (selectedSlots.fullday && both) {
      if (!selectedSlots.morning) out.push({ date: dateStr, slot: 'morning' })
      if (!selectedSlots.afternoon) out.push({ date: dateStr, slot: 'afternoon' })
    }
  }
  return out
}

// Group open dates by month with (M)/(A) annotations. Port of Scale's
// buildAvailabilityReportText.
function buildReportText(rows, mediatorName, includeLegend) {
  const dateMap = {}
  rows.forEach((it) => {
    if (!dateMap[it.date]) dateMap[it.date] = { morning: false, afternoon: false }
    if (it.slot === 'morning') dateMap[it.date].morning = true
    else if (it.slot === 'afternoon') dateMap[it.date].afternoon = true
    else if (it.slot === 'fullday') { dateMap[it.date].morning = true; dateMap[it.date].afternoon = true }
  })
  const monthGroups = {}
  Object.keys(dateMap).sort().forEach((dateStr) => {
    const [, m, d] = dateStr.split('-').map(Number)
    const monthKey = MONTHS[m - 1]
    if (!monthGroups[monthKey]) monthGroups[monthKey] = []
    const { morning, afternoon } = dateMap[dateStr]
    const ann = morning && afternoon ? '' : afternoon ? '(A)' : morning ? '(M)' : ''
    monthGroups[monthKey].push(`${d}${ann}`)
  })
  const name = (mediatorName && String(mediatorName).trim()) || 'Mediator'
  let text = `${name} availability as of ${reportTimestamp()}\n\n`
  const keys = Object.keys(monthGroups)
  if (keys.length) {
    keys.forEach((k, i) => { if (i > 0) text += '\n'; text += `${k}: ${monthGroups[k].join(', ')}\n` })
    if (includeLegend) text += '\n(M): Morning\n(A): Afternoon'
  } else {
    text += 'No available dates found'
  }
  return text
}

/**
 * AvailabilityReportModal — Scale-parity "appointment" variant. Generates a
 * shareable text list of the mediator's open dates over the next N months,
 * filtered by slot, grouped by month, copyable to clipboard.
 */
function AvailabilityReportModal({ caseId, mediatorName, onClose }) {
  const [monthsAhead, setMonthsAhead] = useState(1)
  const [slots, setSlots] = useState({ morning: true, afternoon: true, fullday: true })
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState('')
  const taRef = React.useRef(null)

  const generate = async () => {
    if (!Object.values(slots).some(Boolean)) return
    setLoading(true)
    try {
      const today = new Date()
      const start = ymd(today.getFullYear(), today.getMonth(), today.getDate())
      const endIdx = today.getMonth() + Number(monthsAhead)
      const endY = today.getFullYear() + Math.floor(endIdx / 12)
      const endM = ((endIdx % 12) + 12) % 12
      const end = ymd(endY, endM, new Date(endY, endM + 1, 0).getDate())
      const r = await api.get(`/cases/${caseId}/reschedule/availability`, { params: { start, end } })
      const rows = buildAppointmentReportRows(r.data?.slots || [], slots)
      const onlyFullday = slots.fullday && !slots.morning && !slots.afternoon
      setReport(buildReportText(rows, mediatorName, !onlyFullday))
    } catch (e) {
      setReport('')
    } finally {
      setLoading(false)
    }
  }

  const copy = async () => {
    if (!report) return
    try { await navigator.clipboard.writeText(report) }
    catch {
      const el = taRef.current
      if (el) { el.focus(); el.select(); try { document.execCommand('copy') } catch (_e) { /* manual */ } }
    }
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Availability Report</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Show Availability For The Next</label>
            <select value={monthsAhead} onChange={(e) => setMonthsAhead(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n} month{n === 1 ? '' : 's'}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Slot time</label>
            <div className="flex gap-4 flex-wrap">
              {[['morning', 'Morning'], ['afternoon', 'Afternoon'], ['fullday', 'Full day']].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={slots[k]} onChange={(e) => setSlots((s) => ({ ...s, [k]: e.target.checked }))} className="w-4 h-4" />
                  {l}
                </label>
              ))}
            </div>
          </div>
          <div className="h-px w-full bg-slate-200" />
          {report && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">Offered Dates</label>
                <button type="button" onClick={copy} className="text-xs px-2.5 py-1 rounded-md bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200">Copy Report</button>
              </div>
              <textarea ref={taRef} readOnly value={report} className="w-full h-64 p-3 border border-gray-300 rounded text-sm font-mono bg-gray-50 resize-none" style={{ whiteSpace: 'pre-wrap' }} />
            </div>
          )}
          <div className="flex justify-end gap-3 mt-2">
            <button type="button" onClick={onClose} disabled={loading} className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={generate} disabled={loading} className="px-5 py-2 rounded-md text-white text-sm font-medium disabled:opacity-50" style={{ background: TEAL }}>{loading ? 'Generating…' : 'GO'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function CaseReschedule({ onToast, onRescheduled }) {
  const { caseData } = useContext(CaseDetailContext)
  const caseId = caseData?.id
  const mediatorName = caseData?.mediator?.name || caseData?.mediatorName || caseData?.mediator_name || 'Mediator'

  const [open, setOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [info, setInfo] = useState(null) // { status, currentSlots, account, googleEventId, caseStatus }
  const today = new Date()
  const [viewY, setViewY] = useState(today.getFullYear())
  const [viewM, setViewM] = useState(today.getMonth())
  const [monthSlots, setMonthSlots] = useState([])
  const [selectedDate, setSelectedDate] = useState(null)
  const [dateSlots, setDateSlots] = useState(null)
  const [picked, setPicked] = useState(null) // { title, ids, type, slot_time }
  const [submitting, setSubmitting] = useState(false)
  const [loadingDay, setLoadingDay] = useState(false)

  const openModal = () => {
    setSelectedDate(null); setDateSlots(null); setPicked(null)
    setViewY(today.getFullYear()); setViewM(today.getMonth())
    setOpen(true)
  }
  const closeModal = () => { if (!submitting) setOpen(false) }

  // Load current slot + mediator times when the modal opens.
  useEffect(() => {
    if (!open || !caseId) return
    let alive = true
    api.get(`/cases/${caseId}/reschedule/info`)
      .then((r) => { if (alive) setInfo(r.data) })
      .catch(() => { if (alive) onToast?.({ type: 'error', message: 'Could not load schedule info' }) })
    return () => { alive = false }
  }, [open, caseId])

  // Load the visible month's availability for the calendar.
  useEffect(() => {
    if (!open || !caseId) return
    let alive = true
    const start = ymd(viewY, viewM, 1)
    const end = ymd(viewY, viewM, new Date(viewY, viewM + 1, 0).getDate())
    api.get(`/cases/${caseId}/reschedule/availability`, { params: { start, end } })
      .then((r) => { if (alive) setMonthSlots(r.data?.slots || []) })
      .catch(() => { if (alive) setMonthSlots([]) })
    return () => { alive = false }
  }, [open, caseId, viewY, viewM])

  const weeks = useMemo(() => buildWeeks(viewY, viewM), [viewY, viewM])
  const todayStr = ymd(today.getFullYear(), today.getMonth(), today.getDate())

  const bookingFor = useCallback((dateStr) => {
    const m = monthSlots.find((s) => s.date === dateStr && s.slot === 'morning')
    const a = monthSlots.find((s) => s.date === dateStr && s.slot === 'afternoon')
    const mA = m?.status === 'available'
    const aA = a?.status === 'available'
    if (mA && aA) return 'fullday'
    if (mA) return 'morning'
    if (aA) return 'afternoon'
    return 'booked'
  }, [monthSlots])

  const onDateClick = (dateStr) => {
    setSelectedDate(dateStr); setPicked(null); setDateSlots(null); setLoadingDay(true)
    api.get(`/cases/${caseId}/reschedule/availability`, { params: { date: dateStr } })
      .then((r) => setDateSlots(r.data?.slots || []))
      .catch(() => setDateSlots([]))
      .finally(() => setLoadingDay(false))
  }

  const prevDisabled = viewY < today.getFullYear() || (viewY === today.getFullYear() && viewM <= today.getMonth())
  const goPrev = () => { if (prevDisabled) return; const m = viewM - 1; if (m < 0) { setViewM(11); setViewY((y) => y - 1) } else setViewM(m) }
  const goNext = () => { const m = viewM + 1; if (m > 11) { setViewM(0); setViewY((y) => y + 1) } else setViewM(m) }

  // Build the selectable slot cards for the chosen date (Scale's logic):
  // half-day case → morning/afternoon; full-day case → + Full Day when both open.
  const availableSlots = useMemo(() => {
    if (!dateSlots || !info) return []
    const acct = info.account || {}
    const tz = acct.timezone
    const morning = dateSlots.find((s) => s.slot === 'morning')
    const afternoon = dateSlots.find((s) => s.slot === 'afternoon')
    const out = []
    if (morning?.status === 'available') {
      out.push({ title: `Morning - ${timeLabel(acct.morning_time, tz)}`, ids: [morning.id], type: 'morning', slot_time: morning.slot_time })
    }
    if (afternoon?.status === 'available') {
      out.push({ title: `Afternoon - ${timeLabel(acct.afternoon_time, tz)}`, ids: [afternoon.id], type: 'afternoon', slot_time: afternoon.slot_time })
    }
    if (info.status === 'full' && morning?.status === 'available' && afternoon?.status === 'available') {
      out.push({ title: `Full Day - ${timeLabel(acct.fullday_time, tz)}`, ids: [morning.id, afternoon.id], type: 'fullday', slot_time: acct.fullday_time })
    }
    return out
  }, [dateSlots, info])

  const handleSubmit = async () => {
    if (!picked || !info) return
    setSubmitting(true)
    try {
      const r = await api.patch(`/cases/${caseId}/reschedule`, {
        google_event_id: info.googleEventId,
        selectedAppointment: dateSlots,
        timezone: info.account?.timezone,
        ids: picked.ids,
        oldSlot: info.currentSlots,
      })
      if (r.data?.updatedSlots) {
        onToast?.({ type: 'success', message: 'Case rescheduled successfully!' })
        setOpen(false)
        if (onRescheduled) onRescheduled()
        else setTimeout(() => window.location.reload(), 1200)
      } else {
        onToast?.({ type: 'error', message: 'Unable to reschedule. Please try again.' })
      }
    } catch (err) {
      console.error('Reschedule failed:', err)
      onToast?.({ type: 'error', message: err?.response?.data?.message || 'Failed to reschedule case' })
    } finally {
      setSubmitting(false)
    }
  }

  const niceSelectedDate = selectedDate
    ? (() => { const [y, m, d] = selectedDate.split('-').map(Number); return `${MONTHS[m - 1]} ${pad2(d)}, ${y}` })()
    : ''

  return (
    <div className="w-full m-0 p-0 h-full flex items-stretch">
      <button
        type="button"
        onClick={openModal}
        className="bg-white border border-gray-300 text-gray-600 rounded-md h-11 px-4 py-2 flex justify-between cursor-pointer items-center w-full hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium">Reschedule Case</span>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 flex-shrink-0 text-gray-500">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h5 className="text-lg font-semibold">Reschedule Case</h5>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-700 text-xl leading-none" aria-label="Close">×</button>
            </div>

            <div className="flex flex-col md:flex-row gap-4 p-4">
              {/* Calendar */}
              <div className="flex-1 md:w-[410px] px-1 py-1">
                {/* Availability report (Scale parity) */}
                <section className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg">
                  <div className="min-w-0 flex-1" style={{ flexBasis: 'min(100%, 12rem)' }}>
                    <h3 className="text-xs font-semibold leading-tight text-slate-900">Availability report</h3>
                    <p className="mt-0.5 text-[11px] leading-snug text-slate-500" style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      Availability report: get a shareable list of open dates.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReportOpen(true)}
                    className="shrink-0 rounded-md px-2.5 py-1 text-xs font-medium text-white inline-flex items-center gap-1"
                    style={{ background: TEAL }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="2" width="6" height="4" rx="1" /><path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
                    </svg>
                    Create
                  </button>
                </section>
                <div className="mb-3 mt-0.5 flex items-center gap-2">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-slate-400">Pick a date</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
                <div className="flex justify-between items-center mb-3">
                  <div className="text-base font-semibold">
                    <span className="text-black">{MONTHS[viewM]} </span>
                    <span className="text-gray-500">{viewY}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={goPrev} disabled={prevDisabled} className={`px-2 py-1 text-2xl leading-none ${prevDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700'}`}>‹</button>
                    <button type="button" onClick={goNext} className="px-2 py-1 text-2xl leading-none text-gray-700">›</button>
                  </div>
                </div>
                <div className="grid grid-cols-5 text-center mb-1">
                  {WEEKDAYS.map((w) => (
                    <div key={w} className="h-8 flex items-center justify-center text-xs font-bold text-gray-600">{w}</div>
                  ))}
                </div>
                {weeks.map((week, wi) => (
                  <div className="grid grid-cols-5" key={wi}>
                    {week.map((cell, ci) => {
                      if (!cell) return <div key={`e-${wi}-${ci}`} style={{ height: 44, width: 44, margin: '4px auto' }} />
                      const dateStr = ymd(viewY, viewM, cell.day)
                      const past = dateStr < todayStr
                      const booking = bookingFor(dateStr)
                      return (
                        <CalendarCell key={ci} day={cell.day} booking={booking} disabled={past} onClick={() => onDateClick(dateStr)} />
                      )
                    })}
                  </div>
                ))}
                <div className="flex items-center gap-4 mt-3 px-1">
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-600"><span style={{ width: 12, height: 12, background: GREEN, borderRadius: 3, border: '1px solid #d1d5db' }} /> Available</span>
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-600"><span style={{ width: 12, height: 12, background: RED, borderRadius: 3, border: '1px solid #d1d5db' }} /> Booked</span>
                </div>
              </div>

              {/* Slot picker */}
              <div className="flex-1 md:max-w-[300px] md:border-l md:border-l-gray-200 md:pl-6 min-h-[420px]">
                {!selectedDate && <p className="text-sm text-gray-500 mt-2">Select a date to see available slots.</p>}
                {selectedDate && (
                  <h3 className="text-base font-semibold pb-3">{niceSelectedDate}</h3>
                )}
                {selectedDate && (
                  <>
                    {loadingDay ? (
                      <p className="text-sm text-gray-500">Loading…</p>
                    ) : !picked ? (
                      availableSlots.length > 0 ? (
                        <ul className="list-none mt-1 flex flex-col gap-y-3">
                          {availableSlots.map((slot) => (
                            <li key={slot.title}>
                              <SlotButton title={slot.title} onClick={() => setPicked(slot)} />
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">No slots available</p>
                      )
                    ) : (
                      <div className="flex flex-col gap-3">
                        <SlotButton title={picked.title} />
                        <button
                          type="button"
                          onClick={handleSubmit}
                          disabled={submitting}
                          className="w-full text-white rounded-full h-11 px-8 py-2 font-medium disabled:opacity-50"
                          style={{ background: TEAL }}
                        >
                          {submitting ? 'Rescheduling…' : 'Reschedule'}
                        </button>
                        <button type="button" onClick={() => setPicked(null)} className="text-xs text-gray-500 hover:text-gray-700 underline">Pick a different slot</button>
                        {info?.caseStatus === 'cancelled' && (
                          <div className="p-2 mt-1 rounded-lg border border-gray-300 bg-amber-50">
                            <p className="text-gray-600 text-sm font-medium">
                              Rescheduling a previously canceled case. Parties will receive an email notifying them that the matter has been rescheduled, and notifications will restart.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {reportOpen && (
        <AvailabilityReportModal caseId={caseId} mediatorName={info?.mediatorName || mediatorName} onClose={() => setReportOpen(false)} />
      )}
    </div>
  )
}
